import React from 'react';
import styled, { css } from 'styled-components';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';

interface Width {
    extraLarge: string;
}

interface Height {
    extraLarge?: string;
    large?: string;
    normal?: string;
    medium?: string;
}

interface LeftAlignment {
    extraLarge?: string;
    large?: string;
    normal?: string;
    medium?: string;
}

interface Position {
    xAxis: number | string;
    yAxis: number | string;
}

interface ObjectPosition {
    extraLarge?: Position;
    large?: Position;
    normal?: Position;
    medium?: Position;
}

export interface MediaProps {
    fallbackImageUrl: string;
    videoUrl?: string;
    webpUrl?: string;
    mediaAlign?: 'center' | 'left' | 'right';
    crop?: boolean;
    width?: Width;
    height?: Height;
    verticalCenterAlign?: boolean;
    objectPosition?: ObjectPosition;
    absolutePositioned?: boolean;
    leftAlign?: LeftAlignment;
}

export default function Media({
    fallbackImageUrl,
    videoUrl,
    webpUrl,
    mediaAlign = 'center',
    crop = true,
    width,
    height,
    verticalCenterAlign = true,
    objectPosition,
    absolutePositioned = true,
    leftAlign,
}: MediaProps) {
    return (
        <MediaContainer leftAlign={leftAlign}>
            <picture>
                {videoUrl && <source srcSet={videoUrl} type="video/mp4" />}
                {webpUrl && <source srcSet={webpUrl} type="image/webp" />}
                <Image
                    src={fallbackImageUrl}
                    alt=""
                    crop={crop}
                    mediaAlign={mediaAlign}
                    mediaWidth={width}
                    mediaHeight={height}
                    verticalCenterAlign={verticalCenterAlign}
                    objectPosition={objectPosition}
                    absolutePositioned={absolutePositioned}
                />
            </picture>
        </MediaContainer>
    );
}

const breakpoints = {
    large: 1300,
    extraLarge: 1500,
};

const MediaContainer = styled(Box)<{ leftAlign?: LeftAlignment }>`
    position: relative;
    width: 100%;
    height: 100%;
    ${mediaQuery.only('large')} {
        ${({ leftAlign }) =>
            leftAlign &&
            leftAlign.extraLarge &&
            css`
                left: ${leftAlign.extraLarge};
            `}
    }
    ${mediaQuery.only('normal')} {
        ${({ leftAlign }) =>
            leftAlign &&
            leftAlign.large &&
            css`
                left: ${leftAlign.large};
            `}
    }
    ${mediaQuery.only('medium')} {
        ${({ leftAlign }) =>
            leftAlign &&
            leftAlign.normal &&
            css`
                left: ${leftAlign.normal};
            `}
    }
    ${mediaQuery.only('small')} {
        ${({ leftAlign }) =>
            leftAlign &&
            css`
                left: ${leftAlign.medium};
            `}
    }
    overflow: hidden;
`;

const Image = styled.img<{
    crop?: boolean;
    mediaAlign?: string;
    mediaWidth?: Width;
    mediaHeight?: Height;
    verticalCenterAlign?: boolean;
    objectPosition?: ObjectPosition;
    absolutePositioned: boolean;
}>`
    overflow: hidden;

    ${({ verticalCenterAlign }) =>
        verticalCenterAlign &&
        css`
            top: 50%;
            transform: translateY(-50%);
        `}

    ${({ absolutePositioned }) =>
        absolutePositioned &&
        css`
            position: absolute;
        `}

    ${({ crop }) =>
        css`
            object-fit: ${crop ? 'cover' : 'contain'};
        `}

    ${({ mediaAlign }) =>
        mediaAlign === 'center' &&
        css`
            margin: 0 auto;
            left: 0;
            right: 0;
        `}

    ${({ mediaAlign }) =>
        mediaAlign === 'left' &&
        css`
            left: 0;
        `}

    ${({ mediaAlign }) =>
        mediaAlign === 'right' &&
        css`
            right: 0;
        `}

    ${mediaQuery.between('small', 'medium')} {
        position: relative;
    }

    ${mediaQuery.only('medium')} {
        ${({ objectPosition, crop }) =>
            objectPosition?.medium &&
            crop &&
            css`
                object-position: ${objectPosition?.medium?.xAxis} ${objectPosition?.medium?.yAxis};
            `}

        ${({ mediaHeight }) =>
            mediaHeight?.medium &&
            css`
                min-height: ${mediaHeight?.medium};
            `}
    }

    ${mediaQuery.only('normal')} {
        ${({ objectPosition, crop }) =>
            objectPosition?.normal &&
            crop &&
            css`
                object-position: ${objectPosition?.normal?.xAxis} ${objectPosition?.normal?.yAxis};
            `}

        ${({ mediaHeight }) =>
            mediaHeight?.normal &&
            css`
                height: ${mediaHeight?.normal};
            `}
    }

    @media only screen and (min-width: ${breakpoints.large}px) {
        ${({ objectPosition, crop }) =>
            objectPosition?.large &&
            crop &&
            css`
                object-position: ${objectPosition?.large?.xAxis} ${objectPosition?.large?.yAxis};
            `}

        ${({ mediaHeight }) =>
            mediaHeight?.large &&
            css`
                min-height: ${mediaHeight?.large};
            `}
    }

    @media only screen and (min-width: ${breakpoints.extraLarge}px) {
        ${({ objectPosition, crop }) =>
            objectPosition?.extraLarge &&
            crop &&
            css`
                object-position: ${objectPosition?.extraLarge?.xAxis}
                    ${objectPosition?.extraLarge?.yAxis};
            `}

        ${({ mediaWidth }) =>
            mediaWidth?.extraLarge &&
            css`
                min-width: ${mediaWidth?.extraLarge};
            `}

        ${({ mediaHeight }) =>
            mediaHeight?.extraLarge &&
            css`
                min-height: ${mediaHeight?.extraLarge};
            `}
    }
`;
